/** Shared bootstrapping for display and control pages. */
import { createStorage, browserStorage, type AppStorage } from './storage.js';
import { TimerClient } from './sync.js';
import { createSocket, type ReconnectingSocket } from './socket.js';
import type { Role } from '../../shared/protocol.js';

export interface Booted {
  client: TimerClient;
  socket: ReconnectingSocket | null;
  storage: AppStorage;
  room: string | null;
}

/** Room token: from the URL, otherwise the last one used (PWA start_url has none). */
export function resolveRoom(storage: AppStorage): string | null {
  const fromUrl = new URLSearchParams(location.search).get('room');
  if (fromUrl && fromUrl.length <= 256) {
    storage.saveRoom(fromUrl);
    return fromUrl;
  }
  return storage.loadRoom();
}

export function wsUrl(room: string): string {
  const proto = location.protocol === 'https:' ? 'wss:' : 'ws:';
  return `${proto}//${location.host}/ws?room=${encodeURIComponent(room)}`;
}

export function boot(role: Role): Booted {
  const storage = createStorage(browserStorage());
  const room = resolveRoom(storage);
  const clientId = storage.getOrCreateClientId(role);
  const client = new TimerClient({
    role,
    clientId,
    room: room ?? '',
    getPin: () => storage.loadPin(),
    storage,
  });

  let socket: ReconnectingSocket | null = null;
  if (room) {
    socket = createSocket(wsUrl(room), {
      onConnecting: () => client.handleConnecting(),
      onOpen: () => client.handleOpen(),
      onMessage: (msg) => client.handleMessage(msg),
      onClose: () => client.handleClose(),
    });
    client.attachTransport(socket);
  }

  registerServiceWorker();
  return { client, socket, storage, room };
}

function registerServiceWorker(): void {
  if (!('serviceWorker' in navigator)) return;
  window.addEventListener('load', () => {
    navigator.serviceWorker.register('/sw.js').catch((err) => {
      console.warn('[sw] registration failed', err);
    });
  });
}
